import { useRef, useLayoutEffect, useCallback, RefObject, ReactNode } from 'react';

import { useDebounce } from '@hh.ru/magritte-common-func-utils';
import { useResize } from '@hh.ru/magritte-common-resize';

interface HookArgs {
    descriptionVisible: boolean;
    errorMessageVisible: boolean;
    errorMessage?: ReactNode;
    description?: ReactNode;
    styles: Record<string, string>;
}

export const useFormHelperAnimation = ({
    descriptionVisible,
    errorMessageVisible,
    errorMessage,
    description,
    styles,
}: HookArgs): {
    descriptionRef: RefObject<HTMLDivElement>;
    errorMessageRef: RefObject<HTMLDivElement>;
    helperRef: RefObject<HTMLDivElement>;
} => {
    const helperRef = useRef<HTMLDivElement>(null);
    const descriptionRef = useRef<HTMLDivElement>(null);
    const errorMessageRef = useRef<HTMLDivElement>(null);

    const prevState = useRef({
        description,
        descriptionVisible,
        errorMessage,
        errorMessageVisible,
    });

    const currentlyAnimated = useRef<HTMLDivElement | null>();

    // отдельные коллбеки нужны чтобы можно было их удалить из списка слушателей transitionend при прерывании анимации
    const fadeInFormHelper = useCallback(() => {
        currentlyAnimated.current?.classList.add(styles.formHelperAnimated, styles.formHelperVisible);
        // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    }, []);

    const shrinkFormHelper = useCallback(() => {
        helperRef.current?.style.setProperty('height', '0');
    }, []);

    const setCurrentHeight = useCallback(() => {
        if (helperRef.current) {
            if (descriptionVisible) {
                helperRef.current.style.setProperty('height', `${descriptionRef.current?.clientHeight}px`);
            }
            if (errorMessageVisible) {
                helperRef.current.style.setProperty('height', `${errorMessageRef.current?.clientHeight}px`);
            }
        }
    }, [descriptionVisible, errorMessageVisible]);

    useLayoutEffect(() => {
        if (helperRef.current) {
            // для начала определяем в какой ситуации мы находимся, чтобы понять какую анимацию воспроизвести
            // возможны 3 варианта:
            // подсказка появляется
            const isEmerging =
                (errorMessageVisible && !prevState.current.descriptionVisible) ||
                (descriptionVisible && !prevState.current.errorMessageVisible);

            // одна подсказка заменяется на другую (например появилась ошибка валидации)
            const isReplacing =
                (errorMessageVisible && prevState.current.descriptionVisible) ||
                (descriptionVisible && prevState.current.errorMessageVisible);

            // подсказка исчезает
            const isDissapearing =
                (!errorMessageVisible && prevState.current.errorMessageVisible && !descriptionVisible) ||
                (!descriptionVisible && prevState.current.descriptionVisible && !errorMessageVisible);

            // определяем какой элемент появляется, а какой исчезает
            const emerged = descriptionVisible ? descriptionRef.current : errorMessageRef.current;
            const dissapeared =
                prevState.current.descriptionVisible && !descriptionVisible
                    ? descriptionRef.current
                    : errorMessageRef.current;

            // если у нас есть незавершенные анимации очищаем их слушателей, сами анимации мы переопределим ниже
            helperRef.current.removeEventListener('transitionend', fadeInFormHelper);
            currentlyAnimated.current?.removeEventListener('transitionend', shrinkFormHelper);

            const firstProcessing =
                prevState.current.description === description &&
                prevState.current.descriptionVisible === descriptionVisible &&
                prevState.current.errorMessage === errorMessage &&
                prevState.current.errorMessageVisible === errorMessageVisible;
            helperRef.current?.classList.toggle(styles.containerAnimated, !firstProcessing);

            prevState.current = {
                description,
                descriptionVisible,
                errorMessage,
                errorMessageVisible,
            };

            if (isReplacing) {
                emerged?.classList.add(styles.formHelperAnimated, styles.formHelperVisible);
                dissapeared?.classList.add(styles.formHelperAnimated);
                dissapeared?.classList.remove(styles.formHelperVisible);
                helperRef.current.style.setProperty('height', `${emerged?.clientHeight}px`);
                return;
            }

            if (isEmerging) {
                // случай когда компонент был только примонтирован
                // в этом случае нам не нужно анимировать появление подсказки
                // иначе при загрузке страницы будет много анимаций
                if (firstProcessing) {
                    emerged?.classList.remove(styles.formHelperAnimated);
                    emerged?.classList.add(styles.formHelperVisible);
                    helperRef.current.style.setProperty('height', `${emerged?.clientHeight}px`);
                    return;
                }

                currentlyAnimated.current = emerged;
                helperRef.current.addEventListener('transitionend', fadeInFormHelper, { once: true });
                helperRef.current.style.setProperty('height', `${emerged?.clientHeight}px`);
                return;
            }

            if (isDissapearing) {
                currentlyAnimated.current = dissapeared;
                // обрабатываем отдельно случай когда текст подсказки становится пустым или undefined
                // или предыдцщее состояние не успело примениться
                // в этом случае нет элемента у которого можно анимировать прозрачность
                // а значит некуда добавить и transitionend
                if (dissapeared && !dissapeared.style?.opacity) {
                    dissapeared?.classList.remove(styles.formHelperVisible);
                    shrinkFormHelper();
                    return;
                }
                if (!dissapeared) {
                    shrinkFormHelper();
                    return;
                }
                dissapeared?.addEventListener('transitionend', shrinkFormHelper, { once: true });
                dissapeared?.classList.add(styles.formHelperAnimated);
                dissapeared?.classList.remove(styles.formHelperVisible);
            }
        }
        // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    }, [descriptionVisible, errorMessageVisible, fadeInFormHelper, shrinkFormHelper, errorMessage, description]);

    useResize(useDebounce(setCurrentHeight, 64));

    return {
        descriptionRef,
        errorMessageRef,
        helperRef,
    };
};
